<template>
  <div class="wrap">
    <div class="left">
      <DayPilotNavigator
        :config="navigatorConfig"
        :style="{ width: '100%', height: '100%' }"
        ref="navigatorRef"
      />
    </div>
    <div class="content">
      <DayPilotCalendar :config="calendarConfig" ref="calendarRef" />

      <!-- Summary Section -->
      <div class="summary">
        <div
          class="summary-column"
          :style="{ backgroundColor: setColor(day) }"
          v-for="day in currentWeekDays"
          :key="day.toString('yyyy-MM-dd')"
        >
          <div class="day-name">{{ day.toString("dddd", "pt-pt") }}</div>
          <div class="day-valor">
            Total Valor: {{ dayTotals[day.toString("yyyy-MM-dd")] }}
          </div>
          <button v-if="!isTecnico" @click="validateDay(day)">Agendar</button>
        </div>
      </div>
    </div>
  </div>

  <div class="w-full">
    <createPopUp
      v-if="showCreatePopUp"
      :isTecnico="props.isTecnico"
      :model_data="createModel"
      @save-servico="createServico"
      @close="closeCreatePopUp"
    />
  </div>

  <div class="w-full">
    <popUp
      title="Informação do Planeamento"
      v-if="showModal"
      :edit_mode="popUpEdit_mode"
      :model="modalModel"
      :modelData="modalData"
      :setData="modalSetData"
      @save_form="handleModalSubmit"
      @close="handleModalCancel"
    />
  </div>

  <genericPopUp
    v-if="showPopUp"
    title="Informação do Planeamento"
    :model="{ descricao: planeamento_descricao.descricao }"
    @close="showPopUp = false"
    @save_form="set_descricao"
  />

  <ordemServicoDateTimePopUp
    v-if="showOSDateTimePopUp"
    :model_data="OSDateTime_data"
    @save-data="setOSDateTime"
    @close="showOSDateTimePopUp = false"
  />
</template>

<script setup>
import {
  DayPilot,
  DayPilotCalendar,
  DayPilotNavigator,
} from "@daypilot/daypilot-lite-vue";
import { ref, reactive, onMounted, watch,  computed } from "vue";
import "./css/calendar_green.css";
import "./css/navigator_dark.css";
import popUp from "@/components/popUps/planeamentoPopUp.vue";
import genericPopUp from "@/components/popUps/genericPopUp.vue";
import { hide, start } from "@popperjs/core";
import { toast } from "vue3-toastify";
import axios from "axios";
import { colors, id as loggedID } from "@/extraData";
import { data } from "autoprefixer";
import { ar, id } from "date-fns/locale";
import { sub } from "date-fns";
import { requestLock } from '@/utils/requestLock.js';
import { normalizarCamposData, hojeLocal } from '@/utils/dataBD';
import createPopUp from "@/components/popUps/planeamentoCreatePopUp.vue";
import ordemServicoDateTimePopUp from "@/components/popUps/ordemServicoDateTime.vue";
import { day } from "vue-cal/dist/i18n/ar.cjs";

// Reactive references and state
const planeamento_data = ref([]);
const planeamento_descricao = ref({ planeamento_id: 0, descricao: "" });
const showModal = ref(false);
const showPopUp = ref(false);
const showCreatePopUp = ref(false);
const showOSDateTimePopUp = ref(false);
const OSDateTime_data = ref({ inicio: "", fim: "" });
const modalModel = ref({});
const createModel = ref({});
const modalData = ref({});
const modalSetData = ref({
  zona: { id: 0, nome: "N/A" },
  servico: { id: 0, nome: "N/A" },
});

const popUpEdit_mode = ref(true);

const props = defineProps({
  isTecnico: Boolean,
  tecnico: Object,
  servico: Object,
  agendarSemana: Boolean,
  zona: Object,
});

const emit = defineEmits([
  "updatePlaneamentoData",
  "refreshTable",
  "totalWeekValue",
  "weekValidated",
  "servicoCreated",
]);

const baseURL = process.env.VUE_APP_URL_BASE;

// Define the Custom Locale
const ptPtLocale = {
  locale: "pt-pt",
  dayNames: [
    "Domingo",
    "Segunda-feira",
    "Terça-feira",
    "Quarta-feira",
    "Quinta-feira",
    "Sexta-feira",
    "Sábado",
  ],
  dayNamesShort: ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"],
  monthNames: [
    "Janeiro",
    "Fevereiro",
    "Março",
    "Abril",
    "Maio",
    "Junho",
    "Julho",
    "Agosto",
    "Setembro",
    "Outubro",
    "Novembro",
    "Dezembro",
  ],
  monthNamesShort: [
    "Jan",
    "Fev",
    "Mar",
    "Abr",
    "Mai",
    "Jun",
    "Jul",
    "Ago",
    "Set",
    "Out",
    "Nov",
    "Dez",
  ],
  timePattern: "HH:mm",
  datePattern: "dd/MM/yyyy",
  dateTimePattern: "dd/MM/yyyy HH:mm",
  timeFormat: "Clock24Hours",
  weekStarts: 1, // Monday
};

// Register the Locale
DayPilot.Locale.register(ptPtLocale);

// References to DayPilot components
const calendarRef = ref(null);
const navigatorRef = ref(null);

watch(
  () => props.tecnico,
  (newVal, oldVal) => {
    if (newVal && (!oldVal || newVal.id !== oldVal.id)) {
      console.log("Tecnico changed:", newVal);
      // Clear current events
      calendarRef.value.control.events.list = [];
      if (props.isTecnico) {
        calendarConfig.cellHeight = 30;
      } else {
        calendarConfig.cellHeight = 15;
      }
      loadEvents();
    }
  }
);

watch(
  () => props.servico,
  (newVal) => {
    if (newVal) {
      console.log("Servico changed:", newVal);
      loadServicoEvent();
    }
  }
);

watch(
  () => props.agendarSemana,
  (newVal) => {
    if (newVal) {
      agendarAllDays();
      emit("weekValidated");
    }
  }
);

const agendarAllDays = async () => {
  const days = currentWeekDays.value;
  for (let i = 0; i < days.length; i++) {
    await validateDay(days[i]);
  }
};

// Navigator Configuration
const navigatorConfig = reactive({
  locale: "pt-pt",
  selectMode: "Week",
  showMonths: 1,
  skipMonths: 1,
  weekStarts: 1, // Ensure Navigator weeks start on Monday
  onTimeRangeSelected: (args) => {
    const selectedDate = new DayPilot.Date(args.day);
    const selectedMonday = selectedDate.firstDayOfWeek(1); // Align to Monday
    calendarConfig.startDate = selectedMonday;
  },
  theme: "dark",
});

// Calendar Configuration
const calendarConfig = reactive({
  locale: "pt-pt",
  viewType: "Week",
  scale:"Day",
  startDate: new DayPilot.Date().firstDayOfWeek(1), // Initialize to Monday
  durationBarVisible: false,
  businessBeginsHour: 8,
  businessEndsHour: 19,
  businessOnly: true,
  timeRangeSelectedHandling: "Enabled",
  //if its tecnico the cellHeight is 30
  cellHeight: props.isTecnico ? 30 : 15,

  
  
  //hourWidth: 45,

  events: [],
  weekStarts: 1, // Ensure Calendar weeks start on Monday
  onTimeRangeSelected: async (args) => {
    // if (!props.tecnico || props.tecnico.id === 0) {
    //   toast("Escolha um Técnico!", {
    //     theme: "auto",
    //     type: "warning",
    //     position: "top-center",
    //     autoClose: 1000,
    //   });
    //   calendarRef.value.control.clearSelection();
    //   return;
    // }
    const dateNow = hojeLocal();
    console.log("Data selecionada", args.start);
    console.log("Data atual", dateNow);
    if (args.start < dateNow) {
      toast("Não é permitido agendar eventos no passado!", {
        theme: "auto",
        type: "warning",
        position: "top-center",
        autoClose: 2000,
      });
      calendarRef.value.control.clearSelection();
      return;
    }
    await createEvent(args.start, args.end, args.resource);
    calendarRef.value.control.clearSelection();
  },
  onEventClick: (args) => {
    if (!props.tecnico || props.tecnico.id === 0) return;
    const event = args.e;
    const index = planeamento_data.value.findIndex(
      (item) => item.event_id === event.data.id
    );
    if (index !== -1) {
      //const statusList = ['confirmado', 'fechado', 'rejeitado', 'cancelado', 'interrompido'];

      const statusList = [];
      if (statusList.includes(planeamento_data.value[index].status)) {
        toast("Não é permitido editar este evento!", {
          theme: "auto",
          type: "warning",
          position: "top-center",
          autoClose: 2000,
        });
        return;
      }
    }
    editEvent(args.e);
  },
  onEventResized: async (args) => {
    await handleEventTimeChange(args.e);
  },
  onEventMoved: async (args) => {
    await handleEventTimeChange(args.e);
  },
  onEventRemoved: async (args) => {
    await handleEventDeletion(args.e);
  },

  onEventMoving: (args) => {
    const event = args.e;
    const index = planeamento_data.value.findIndex(
      (item) => item.event_id === event.data.id
    );
    if (index !== -1) {
      const statusList = [
        "confirmado",
        "fechado",
        "rejeitado",
        "cancelado",
        "interrompido",
        "pendente",
      ];
      if (statusList.includes(planeamento_data.value[index].status)) {
        toast("Não é permitido mover este evento!", {
          theme: "auto",
          type: "warning",
          position: "top-center",
          autoClose: 2000,
        });
        args.preventDefault(); // Prevents the visual change
      }
    }
  },

  onEventResizing: (args) => {
    const event = args.e;
    const index = planeamento_data.value.findIndex(
      (item) => item.event_id === event.data.id
    );
    if (index !== -1) {
      const statusList = [
        "confirmado",
        "fechado",
        "rejeitado",
        "cancelado",
        "interrompido",
        "pendente",
      ];
      if (statusList.includes(planeamento_data.value[index].status)) {
        toast("Não é permitido redimensionar este evento!", {
          theme: "auto",
          type: "warning",
          position: "top-center",
          autoClose: 2000,
        });
        args.preventDefault(); // Prevents the visual change
      }
    }
  },

  contextMenu: props.isTecnico
    ? null
    : new DayPilot.Menu({
        onShow: (args) => {
          const event = args.source;
          const planeamento = planeamento_data.value.find(
            (item) => item.event_id === event.data.id
          );
          if (props.isTecnico) {
            args.menu.items.map((item) => (item.disabled = true));
          } else if (planeamento) {
            const status = planeamento.status;
            // Enable "Confirmar" only if status is 'agendado'
            const confirmarItem = args.menu.items.find(
              (item) => item.text === "Confirmar"
            );
            if (confirmarItem) {
              confirmarItem.disabled = status !== "agendado";
            }
            // Hide "Eliminar" if status is 'confirmado'
            const eliminarItem = args.menu.items.find(
              (item) => item.text === "Eliminar"
            );
            if (eliminarItem) {
              eliminarItem.disabled = status === "confirmado";
            }

            const rejeitarItem = args.menu.items.find(
              (item) => item.text === "Rejeitar"
            );
            if (rejeitarItem) {
              //rejeitarItem.disabled = status !== 'agendado';
              rejeitarItem.disabled =
                status == "ativo" || status == "atribuido" || status =="rejeitado";
            }
            const cancelarItem = args.menu.items.find(
              (item) => item.text === "Cancelar"
            );
            if (cancelarItem) {
              //cancelarItem.disabled = status !== 'agendado';
              cancelarItem.disabled =
                status == "ativo" || status == "atribuido" || status == 'cancelado';
            }
            const mudarHorarioItem = args.menu.items.find(
              (item) => item.text === "Mudar Horário"
            );
            if (mudarHorarioItem) {
              mudarHorarioItem.disabled =
                status == "confirmado" ||
                status == "fechado" ||
                status == "rejeitado" ||
                status == "cancelado" ||
                status == "interrompido" ||
                status == "pendente";
            }
            if(status == 'pendente'){
              //disable all items
              args.menu.items.map((item) => (item.disabled = true));
              // toast("Aguarde a confirmação do cliente!", {
              //   theme: "auto",
              //   type: "warning",
              //   position: "top-center",
              //   autoClose: 2000,
              // });
            }
          } else {
            console.log("Evento não encontrado em planeamento_data.");
          }
        },
        items: [
          {
            text: "Confirmar",
            onClick: async (args) => {
              if (!props.tecnico || props.tecnico.id === 0) return;
              await handleEventConfirmation(args.source);
            },
          },
          {
            text: "Cancelar",
            backColor: "#999999",
            onClick: async (args) => {
              if (!props.tecnico || props.tecnico.id === 0) return;
              await handleEventAction(args.source, "cancelado");
            },
          },
          {
            text: "Rejeitar",
            onClick: async (args) => {
              if (!props.tecnico || props.tecnico.id === 0) return;
              await handleEventAction(args.source, "rejeitado");
            },
          },
          {
            text: "Eliminar",
            onClick: async (args) => {
              if (!props.tecnico || props.tecnico.id === 0) return;
              const dp = calendarRef.value.control;
              dp.events.remove(args.source);
              await handleEventDeletion(args.source);
            },
          },
          {
            text: "Mudar Horário",
            onClick: async (args) => {
              if (!props.tecnico || props.tecnico.id === 0) return;
              const event = args.source;
              const index = planeamento_data.value.findIndex(
                (item) => item.event_id === event.data.id
              );
              openOSDateTimePopUp(event.data.id);
            },
          },
        ],
      }),
  onBeforeEventRender: (args) => {
    if (!props.tecnico || props.tecnico.id === 0) return;

    const statusList = [
      "confirmado",
      "fechado",
      "rejeitado",
      "cancelado",
      "interrompido",
      "pendente"
    ];
    const isDisabled = statusList.includes(args.data.status) || props.isTecnico;
    args.data.moveDisabled = isDisabled;
    args.data.resizeDisabled = isDisabled;

    args.data.areas = [
      {
        right: 5,
        top: 7,
        width: 25,
        height: 25,
        backColor: "#ffffff",
        fontColor: "#666666",
        padding: 4,
        symbol: "/icons/daypilot.svg#threedots-v",
        style: "border-radius: 50%;",
        action: "ContextMenu",
      },
    ];
  },
  theme: "calendar_green",
});

// Computed Properties
const currentWeekDays = computed(() => {
  const days = [];
  const start = new DayPilot.Date(calendarConfig.startDate).firstDayOfWeek(1); // Ensure start on Monday
  for (let i = 0; i < 7; i++) {
    days.push(start.addDays(i));
  }
  return days;
});

const dayTotals = computed(() => {
  const totals = {};
  currentWeekDays.value.forEach((day) => {
    const dayStr = day.toString("yyyy-MM-dd");
    const events = planeamento_data.value.filter((item) => {
      const eventStart = new DayPilot.Date(item.inicio);
      // only count statuses that are not "cancelado" or "rejeitado"
      return (
        eventStart.getDatePart().toString("yyyy-MM-dd") === dayStr &&
        !["cancelado", "rejeitado","fechado"].includes(item.status)
      );
    });
    // Round total to 2 decimal places
    totals[dayStr] = parseFloat(
      events.reduce((sum, val) => sum + val.valor, 0).toFixed(2)
    );
  });
  return totals;
});

const totalWeekValue = computed(() => {
  return Object.values(dayTotals.value).reduce((sum, val) => sum + val, 0);
});

// Watcher to emit totalWeekValue
watch(totalWeekValue, (newVal) => {
  const roundedValue = parseFloat(newVal.toFixed(2)); // Round to 2 decimal places
  emit("totalWeekValue", roundedValue);
});

// Methods
const validateDay = async (day) => {
  const dayStr = day.toString("yyyy-MM-dd");

  // Filter the items that match the selected day
  const itemsToUpdate = planeamento_data.value.filter((item) => {
    const eventStart = new DayPilot.Date(item.inicio);
    return eventStart.getDatePart().toString("yyyy-MM-dd") === dayStr;
  });

  // Create an array of update promises
  const updatePromises = itemsToUpdate.map(async (item) => {
    if (
      item.status === "agendado" ||
      item.status === "cancelado" ||
      item.status === "rejeitado" ||
      item.status === "interrompido" ||
      item.status === "confirmado" ||
      item.status === "fechado" ||
      item.status === "pendente"
    ) {
      if(item.status == 'pendente'){
        toast("A Encomenda Precisa da aprovação financeira!", {
          theme: "auto",
          type: "warning",
          position: "top-center",
          autoClose: 2000,
        });
      
      }
      return;
    }
    const res = await update(
      `${baseURL}/api/v1/planeamento/editar/${item.id}/`,
      {
        ...item,
        status: "agendado",
      }
    );
    if (res) {
      calendarConfig.events = calendarConfig.events.map((event) => {
        if (event.id === item.event_id) {
          event.backColor = "#ff9b05";
          event.status = "agendado";
        }
        return event;
      });

      // Show a confirmation toast
      if (item.ordemServico_id === 0) {
        toast(`Dia ${day.toString("dd/MM/yyyy")} agendado!`, {
          theme: "auto",
          type: "success",
          position: "top-center",
          autoClose: 2000,
        });
      } else {
        const updatedServico = await update(
          `${baseURL}/api/v1/ordemservico/editar/${item.ordemServico_id}/`,
          {
            data_inicio: item.inicio,
            data_fim: item.fim,
            status: "agendado",
          }
        );
        if (updatedServico) {
          toast(`Dia ${day.toString("dd/MM/yyyy")} agendado!`, {
            theme: "auto",
            type: "success",
            position: "top-center",
            autoClose: 2000,
          });
          emit("refreshTable");
        }
      }
      planeamento_data.value = planeamento_data.value.map((p) => {
        if (p.id === item.id) {
          return {
            ...p,
            status: "agendado",
          };
        }
        return p;
      });

      checkTotalDay(day);
    } else {
      // Show an error toast
      toast(`Erro ao agendar o dia ${day.toString("dd/MM/yyyy")}!`, {
        theme: "auto",
        type: "error",
        position: "top-center",
        autoClose: 2000,
      });
    }
  });
  // Wait for all updates to complete
  await Promise.all(updatePromises);
};

const openOSDateTimePopUp = (event_id) => {
  const event = calendarConfig.events.find((item) => item.id === event_id);
  OSDateTime_data.value = {
    inicio: event.start.toString(),
    fim: event.end.toString(),
    event_id: event_id,
  };
  showOSDateTimePopUp.value = true;
};

const setOSDateTime = async (data) => {
  // if the date and tiem are diferent i will update the ordem de servico, the planeam,ento
  //as well as the event in the calendar
  console.log("date and time", data);
  //the inicio and fim must include the seconds
  if (data.inicio.length < 19 || data.fim.length < 19) {
    data.inicio = data.inicio.slice(0, 16) + ":00";
    data.fim = data.fim.slice(0, 16) + ":00";
  }

  const event = calendarConfig.events.find((item) => item.id === data.event_id);
  if (
    event.start.toString() !== data.inicio ||
    event.end.toString() !== data.fim
  ) {
    const planeamento_index = planeamento_data.value.findIndex(
      (item) => item.event_id === data.event_id
    );
    if (planeamento_index !== -1) {
      const updatedPlaneamento = await update(
        `${baseURL}/api/v1/planeamento/editar/${planeamento_data.value[planeamento_index].id}/`,
        {
          inicio: data.inicio,
          fim: data.fim,
        }
      );
      if (updatedPlaneamento) {
        //Update the local planeamento_data with the new dates
        planeamento_data.value[planeamento_index] = {
          ...planeamento_data.value[planeamento_index],
          inicio: data.inicio,
          fim: data.fim,
        };
        //updtae servico
        const updatedServico = await update(
          `${baseURL}/api/v1/ordemservico/editar/${planeamento_data.value[planeamento_index].ordemServico_id}/`,
          {
            data_inicio: data.inicio,
            data_fim: data.fim,
          }
        );
        if (updatedServico) {
          toast(`Planeamento atualizado!`, {
            theme: "auto",
            type: "success",
            position: "top-center",
            autoClose: 2000,
          });
          emit("refreshTable");
        }
        calendarConfig.events = calendarConfig.events.map((e) => {
          if (e.id === data.event_id) {
            return {
              ...e,
              start: data.inicio,
              end: data.fim,
            };
          }
          return e;
        });
        calendarRef.value.control.update();
        //navegar para a data do evento
        // Navegar para a data  do evento (alinhada à segunda-feira)
        const eventStartDate = new DayPilot.Date(data.inicio).firstDayOfWeek(1);
        calendarConfig.startDate = eventStartDate;

        if (navigatorRef.value && navigatorRef.value.control) {
          navigatorRef.value.control.select(eventStartDate);
        }

        if (calendarRef.value && calendarRef.value.control) {
          calendarRef.value.control.update();
        }
        const newDay = new DayPilot.Date(data.inicio).getDatePart();
        checkTotalDay(newDay);

        showOSDateTimePopUp.value = false;
        
      } else {
        toast(`Erro ao atualizar o planeamento!`, {
          theme: "auto",
          type: "error",
          position: "top-center",
          autoClose: 2000,
        });
      }
    }
  }
};

const createEvent = async (start, end, resource) => {
  // modalSetData.value = {
  //   zona: { id: props.zona.id ? props.zona.id : 0, nome: props.zona?.nome ? props.zona.nome : 'N/A' },
  //   servico: { id: props.servico.id ? props.servico.id : 0, nome: props.servico?.Familia?.nome ? props.servico.Familia.nome : 'N/A' },
  // };
  // modalModel.value = [
  //   { name: "Serviço", id: "service", hidden: true },
  //   { name: "Inicio", id: "start", type: "datetime", disabled: true, hidden: false },
  //   { name: "Fim", id: "end", type: "datetime", disabled: true, hidden: false },
  //   { name: "Zona", id: "location", hidden: true },
  //   { name: "Descrição", id: "description", type: "textarea", hidden: false }
  // ];

  // modalData.value = {
  //   start: formatDateTime(start),
  //   end: formatDateTime(end),
  //   service: props.servico?.Familia?.nome ? props.servico.Familia.nome : 'N/A',
  //   location: props.zona?.nome ? props.zona.nome : 'N/A',
  //   description: "",
  // };
  createModel.value = {
    inicio: formatDateTime(start),
    fim: formatDateTime(end),
    tecnico: props.tecnico ? props.tecnico : { id: 0, nome: "" },
    local: { id: 0, nome: "" },
    familia: { id: 0, nome: "" },
    servico: { id: 0, nome: "" },
    modoContato: { id: 0, nome: "" },
    quantidade: 1,
    valor: 0,
    desconto: 0,
  };
  showCreatePopUp.value = true;
  //showModal.value = true;
};

const editEvent = async (e) => {
  console.log("Edit event", e);
  modalModel.value = [
    { name: "Serviço", id: "service" },
    { name: "Servico", id: "Servico", type: "text", disabled: true },
    { name: "Inicio", id: "start", type: "datetime", disabled: true },
    { name: "Fim", id: "end", type: "datetime", disabled: true },
    { name: "Zona", id: "location" },
    { name: "Descrição", id: "description", type: "textarea" },
    { name: "Cliente", id: "Cliente", type: "text", disabled: true },
    { name: "Subcliente", id: "SubCliente", type: "text", disabled: true },
    { name: "Local", id: "Local", type: "text", disabled: true },
    { name: "Endereço", id: "Endereco", type: "text", disabled: true },
    { name: "Valor", id: "Valor", type: "text", disabled: true },
    { name: "Status", id: "status", type: "text", disabled: true },
  ];
  modalData.value = {
    ...e.data,
    Servico: e.data.Servico,
    start: formatDateTime(e.data.start),
    end: formatDateTime(e.data.end),
    Cliente: e.data.Cliente,
    SubCliente: e.data.SubCliente,
    Local: e.data.Local,
    Endereco: e.data.Endereco,
    Valor: e.data.Valor,
    status: e.data.status,
  };

  modalSetData.value = {
    zona:
      e.data.location === ""
        ? { id: 0, nome: "N/A" }
        : planeamento_data.value.find((item) => item.event_id === e.data.id)
            ?.zona || { id: 0, nome: "N/A" },
    servico:
      e.data.service === ""
        ? { id: 0, nome: "N/A" }
        : planeamento_data.value.find((item) => item.event_id === e.data.id)
            ?.servico || { id: 0, nome: "N/A" },
  };
  planeamento_descricao.value = {
    planeamento_id: e.data.id,
    descricao: e.data.description,
  };
  //showPopUp.value = true;
  if (
    e.data.status == "confirmado" ||
    e.data.status == "fechado" ||
    e.data.status == "rejeitado" ||
    e.data.status == "cancelado" ||
    e.data.status == "interrompido" ||
    e.data.status == "pendente"
  ) {
    // if(e.data.status == 'pendente'){
    //   toast("Aguarde a confirmação do cliente!", {
    //     theme: "auto",
    //     type: "warning",
    //     position: "top-center",
    //     autoClose: 2000,
    //   });
    // }
    popUpEdit_mode.value = false;
  } else {
    popUpEdit_mode.value = true;
  }
  showModal.value = true;
};

const loadEvents = async () => {
  if (!props.tecnico || !props.tecnico.id) {
    console.log("Nenhum técnico fornecido.");
    return;
  }
  // if is tecnico change the cellHeight to 30
  
  console.log("base url", baseURL);
  const planeamentos = await select_all(
    `${baseURL}/api/v1/planeamento/tecnico/${props.tecnico.id}/`
  );
  if (planeamentos) {
    // Mantém a hora literal da BD (ver utils/dataBD.js)
    planeamento_data.value = planeamentos.map((p) => normalizarCamposData(p));
    console.log("Dados de planeamento", planeamento_data.value);
    const colors = {
      pendente:"#dbb73e",
      atribuido: "#968bde",
      agendado: "#ff9b05",
      cancelado: "#999999",
      rejeitado: "#970000",
      interrompido: "#c795d8",
      confirmado: "#01BA32",
      fechado: "black",
    };

    let events = {};

    if (props.isTecnico)
      events = planeamento_data.value
        .filter(
          (item) =>
            item.status == "confirmado" ||
            item.status == "fechado" ||
            item.status == "atribuido"
        )
        .map((item) => {
          return {
            id: item.event_id,
            start: item.inicio,
            end: item.fim,
            Servico: item.titulo.slice(
              item.titulo.indexOf("Serviço:"),
              item.titulo.indexOf("SubCliente:")
            ),
            Cliente: item.titulo.slice(
              item.titulo.indexOf("Cliente:"),
              item.titulo.indexOf("SubCliente:")
            ),
            SubCliente: item.titulo.slice(
              item.titulo.indexOf("SubCliente:"),
              item.titulo.indexOf("Local:")
            ),
            Local: item.titulo.slice(
              item.titulo.indexOf("Local:"),
              item.titulo.indexOf("Endereço:")
            ),
            Endereco: item.titulo.slice(
              item.titulo.indexOf("Endereço:"),
              item.titulo.indexOf("Valor:")
            ),
            Valor: item.titulo.slice(item.titulo.indexOf("Valor:")),
            service: item.servico.nome,
            location: item.zona.nome,
            text: item.titulo,
            description: item.descricao,
            status: item.status, // Include status here
            backColor: colors[item.status],
            status: item.status,
            // Remove moveDisabled and resizeDisabled here
          };
        });
    else
      events = planeamento_data.value.map((item) => {
        return {
          id: item.event_id,
          start: item.inicio,
          end: item.fim,
          Servico: item.titulo.slice(
            item.titulo.indexOf("Serviço:"),
            item.titulo.indexOf("SubCliente:")
          ),
          Cliente: item.titulo.slice(
            item.titulo.indexOf("Cliente:"),
            item.titulo.indexOf("SubCliente:")
          ),
          SubCliente: item.titulo.slice(
            item.titulo.indexOf("SubCliente:"),
            item.titulo.indexOf("Local:")
          ),
          Local: item.titulo.slice(
            item.titulo.indexOf("Local:"),
            item.titulo.indexOf("Endereço:")
          ),
          Endereco: item.titulo.slice(
            item.titulo.indexOf("Endereço:"),
            item.titulo.indexOf("Valor:")
          ),
          Valor: item.titulo.slice(item.titulo.indexOf("Valor:")),
          service: item.servico.nome,
          location: item.zona.nome,
          text: item.titulo,
          description: item.descricao,
          status: item.status, // Include status here
          backColor: colors[item.status],
          status: item.status,
          // Remove moveDisabled and resizeDisabled here
        };
      });

    calendarConfig.events = events;

    // Navegar para o último evento
    if (events.length > 0) {
      // Ordenar eventos por data de início em ordem decrescente
      events.sort((a, b) => new Date(b.start) - new Date(a.start));
      const latestEvent = events[0];

      // Definir a data de início do calendário para a segunda-feira da semana do último evento
      const latestEventDate = new DayPilot.Date(
        latestEvent.start
      ).firstDayOfWeek(1);
      calendarConfig.startDate = latestEventDate;

      // Usar o navigatorRef para selecionar a data
      if (navigatorRef.value && navigatorRef.value.control) {
        navigatorRef.value.control.select(latestEventDate);
      }

      // Atualizar o controle do calendário
      if (calendarRef.value && calendarRef.value.control) {
        calendarRef.value.control.update();
      }
    }
  }
  calendarRef.value.control.update();
};

const loadServicoEvent = async () => {
  if (!props.servico || !props.servico.id) {
    return;
  }

  if (!props.tecnico || !props.tecnico.id) {
    //mesmo se nao tiver o tecnico mover para a semana do servico
    const start = new DayPilot.Date(props.servico.data_inicio).firstDayOfWeek(
      1
    );
    calendarConfig.startDate = start;
    if (navigatorRef.value && navigatorRef.value.control) {
      navigatorRef.value.control.select(start);
    }
    if (calendarRef.value && calendarRef.value.control) {
      calendarRef.value.control.update();
    }
    return;

    // toast("Escolha um Técnico!", {
    //   theme: "auto",
    //   type: "warning",
    //   position: "top-center",
    //   autoClose: 1000,
    // });
  }

  const servico = props.servico;
  // Buscar todos os planeamentos para o técnico selecionado
  let planeamentos = [];

  try {
    const res = await select_all(
      `${baseURL}/api/v1/planeamento/tecnico/${props.tecnico.id}/`
    );
    // Mantém a hora literal da BD (ver utils/dataBD.js)
    planeamentos = (res || []).map((p) => normalizarCamposData(p));
  } catch (error) {
    console.error("Erro ao buscar planeamentos:", error);
  }

  // Verificar se já existe um planeamento para este serviço
  let existingPlaneamento = planeamentos.find(
    (p) => p.servico_id === servico.id
  );

  if (
    existingPlaneamento &&
    servico.status !== "remarcar" &&
    servico.status !== "ativo"
  ) {
    // Planeamento já existe
    console.log("Planeamento para o serviço já existe:", existingPlaneamento);

    // Adicionar a planeamento_data se ainda não estiver presente
    if (!planeamento_data.value.some((p) => p.id === existingPlaneamento.id)) {
      planeamento_data.value.push(existingPlaneamento);
    }

    // Verificar se o evento já existe no calendário
    const eventExists = calendarConfig.events.some(
      (e) => e.id === existingPlaneamento.event_id
    );
    if (!eventExists) {
      // Criar evento a partir dos dados existentes do planeamento
      const event = {
        id: existingPlaneamento.event_id,
        start: existingPlaneamento.inicio,
        end: existingPlaneamento.fim,
        Servico: existingPlaneamento.titulo.slice(
          existingPlaneamento.titulo.indexOf("Serviço:"),
          existingPlaneamento.titulo.indexOf("SubCliente:")
        ),
        Cliente: existingPlaneamento.titulo.slice(
          existingPlaneamento.titulo.indexOf("Cliente:"),
          existingPlaneamento.titulo.indexOf("SubCliente:")
        ),
        SubCliente: existingPlaneamento.titulo.slice(
          existingPlaneamento.titulo.indexOf("SubCliente:"),
          existingPlaneamento.titulo.indexOf("Local:")
        ),
        service: servico.Familia.nome,
        location: servico.Negocio.localInstalacao,
        text: existingPlaneamento.titulo,
        description: existingPlaneamento.descricao,
        status: existingPlaneamento.status,
      };
      calendarConfig.events.push(event);
    }

    // Navegar para a data do evento (alinhada à segunda-feira)
    const eventStartDate = new DayPilot.Date(
      existingPlaneamento.inicio
    ).firstDayOfWeek(1);
    calendarConfig.startDate = eventStartDate;

    if (navigatorRef.value && navigatorRef.value.control) {
      navigatorRef.value.control.select(eventStartDate);
    }

    if (calendarRef.value && calendarRef.value.control) {
      calendarRef.value.control.update();
    }
  } else {
    // Planeamento não existe, criar
    const start = servico.data_inicio;
    const end = servico.data_fim;
    const servicoId = servico.id;
    let endereco = "";
    if (servico.endereco.id != 0) {
      if (servico.endereco?.morada) {
        endereco += servico.endereco.morada;
      }
      if (servico.endereco?.distrito) {
        endereco += ", " + servico.endereco.distrito;
      }
      if (servico.endereco?.codigoPostal) {
        endereco += "," + servico.endereco.codigoPostal;
      }
    } else {
      endereco = "Sem Endereço";
    }

    //servico.endereco.id!=0 ? servico.endereco?.morada + ', ' + servico.endereco?.distrito + '-' + servico.endereco?.codigoPostal:'Sem Endereço';

    const titulo = `Serviço: ${servico.Familia.nome}\n
      Cliente: ${servico.cliente.cliente}\n
      SubCliente: ${servico.subCliente.nome}\n
      Local: ${servico.Negocio.localInstalacao}\n
      Endereço: ${endereco}\n
      Valor: ${props.servico.valor_previsto}
      `;
    const descricao = servico.Negocio.descricao || "";
    const utilizadorId = props.tecnico.id;
    const tecnicoId = props.tecnico.id;
    const eventId = DayPilot.guid();
    const valor = props.servico.valor_previsto || 0;

    const event = {
      id: eventId,
      start: start,
      end: end,
      Servico: servico.Familia.nome,
      Cliente: servico.cliente.cliente,
      SubCliente: servico.subCliente.nome,
      Local: servico.Negocio.localInstalacao,
      Endereco: `${endereco}`,
      Valor: valor,
      text: titulo,
      description: descricao,
      service: servico.Familia.nome,
      location: servico.Negocio.localInstalacao,
      status: "atribuido",
    };

    // Adicionar o evento ao calendário
    calendarConfig.events.push(event);

    const planeamentoData = {
      event_id: eventId,
      titulo: event.text,
      inicio: event.start,
      fim: event.end,
      zona_id: props.zona.id,
      servico_id: servicoId,
      descricao: event.description,
      utilizador_id: utilizadorId,
      tecnico_id: tecnicoId,
      ordemServico_id: servico.id,
      data: new Date().toISOString(),
      valor: valor,
      status: "atribuido",
    };

    const res = await create(
      planeamentoData,
      `${baseURL}/api/v1/planeamento/registrar/`
    );
    if (res) {
      console.log("Planeamento criado com sucesso:", res);
      planeamento_data.value.push({
        ...res,
        zona: { id: props.zona.id, nome: props.zona.nome },
        servico: { id: servicoId, nome: servico.Familia.nome },
      });
      const updatedServico = await update(
        `${baseURL}/api/v1/ordemservico/editar/${servico.id}/`,
        {
          utilizador_id: props.tecnico.id,
          status:
            servico.status == "ativo" || servico.status == "remarcar"
              ? "atribuido"
              : servico.status,
        }
      );
      if (updatedServico) {
        emit("refreshTable");
      }
    } else {
      console.error("Falha ao criar planeamento.");
    }

    // Navegar para a data  do evento (alinhada à segunda-feira)
    const eventStartDate = new DayPilot.Date(event.start).firstDayOfWeek(1);
    calendarConfig.startDate = eventStartDate;

    if (navigatorRef.value && navigatorRef.value.control) {
      navigatorRef.value.control.select(eventStartDate);
    }

    if (calendarRef.value && calendarRef.value.control) {
      calendarRef.value.control.update();
    }
  }
};

const handleModalCancel = () => {
  showModal.value = false;
  calendarRef.value.control.clearSelection();
};

const handleModalSubmit = async (data, setData) => {
  data.start = DayPilot.Date.parse(data.start, "yyyy-MM-ddTHH:mm");
  data.end = DayPilot.Date.parse(data.end, "yyyy-MM-ddTHH:mm");
  data.location = setData.zona.nome;
  data.service = setData.servico.nome;
  console.log("Dados do popup", data);
  console.log("Set Data do popup", setData);
  //data.text = `Serviço: ${data.service}.\nZona: ${data.location}\nValor: ${props.servico.valor}`;

  if (data.id) {
    // Atualizar evento existente no calendário
    calendarRef.value.control.events.update(data);

    // Encontrar e atualizar a entrada correspondente em planeamento_data
    const index = planeamento_data.value.findIndex(
      (item) => item.event_id === data.id
    );
    if (index !== -1) {
      planeamento_data.value[index] = {
        ...planeamento_data.value[index],
        //titulo: data.text,
        //inicio: data.start.value,
        //fim: data.end.value,
        //zona_id: setData.zona.id,
        //servico_id: setData.servico.id,
        descricao: data.description,
        //utilizador_id: props.tecnico.id,
        //tecnico_id: props.tecnico.id,
        // ordemServico_id: props.servico.id,
        //data: new Date().toISOString(),
        //valor: props.servico.valor,
      };
      const res = await update(
        `${baseURL}/api/v1/planeamento/editar/${planeamento_data.value[index].id}/`,
        planeamento_data.value[index]
      );
      if (res) {
        console.log("Dados atualizados com sucesso:", res);
        //update the description in the event
        // calendarConfig.events = calendarConfig.events.map(event => {
        //   if (event.id === data.id) {
        //     event.description = data.description;
        //   }
        //   return event;
        // });
      }
    } else {
      // Se não encontrado, adicionar a planeamento_data
      //   const new_data = {
      //     event_id: data.id,
      //     //titulo: data.text,
      //     inicio: data.start.value,
      //     fim: data.end.value,
      //     zona_id: setData.zona.id,
      //     servico_id: setData.servico.id,
      //     descricao: data.description,
      //     utilizador_id: props.tecnico.id,
      //     tecnico_id: props.tecnico.id,
      //     ordemServico_id: props.servico.id,
      //     data: new Date().toISOString(),
      //     valor: props.servico.valor,
      //   };
      //   const res = await create(new_data, `${baseURL}/api/v1/planeamento/registrar/`);
      //   if (res) {
      //     console.log("Dados criados com sucesso:", res);
      //     planeamento_data.value.push({
      //       ...res,
      //       zona: setData.zona,
      //       servico: setData.servico,
      //     });
      //   }
    }
  } else {
    // Adicionar novo evento ao calendário
    //   data.id = DayPilot.guid();
    //   calendarRef.value.control.events.add({ ...data, id: data.id });
    //   const new_data = {
    //     event_id: data.id,
    //     //titulo: data.text,
    //     inicio: data.start.value,
    //     fim: data.end.value,
    //     zona_id: setData.zona.id,
    //     servico_id: setData.servico.id,
    //     descricao: data.description,
    //     utilizador_id: props.tecnico.id,
    //     tecnico_id: props.tecnico.id,
    //     ordemServico_id: props.servico.id,
    //     data: new Date().toISOString(),
    //     valor: props.servico.valor,
    //     status: "atribuido"
    //   };
    //   const res = await create(new_data, `${baseURL}/api/v1/planeamento/registrar/`);
    //   if (res) {
    //     console.log("Dados criados com sucesso:", res);
    //     planeamento_data.value.push({
    //       ...res,
    //       zona: setData.zona,
    //       servico: setData.servico,
    //     });
    //   }
  }

  showModal.value = false;
  console.log("Dados de planeamento", planeamento_data.value);
  emit("refreshTable");
};

const handleEventTimeChange = async (event) => {
  const data = event.data;
  const index = planeamento_data.value.findIndex(
    (item) => item.event_id === data.id
  );
  if (index !== -1) {
    const statusList = [
      "confirmado",
      "fechado",
      "rejeitado",
      "cancelado",
      "interrompido",
      "pendente"
    ];
    if (statusList.includes(planeamento_data.value[index].status)) {
      toast("Não é permitido alterar o horário deste evento!", {
        theme: "auto",
        type: "warning",
        position: "top-center",
        autoClose: 2000,
      });
      // Revert the event to its original time
      calendarRef.value.control.events.update(data);
      return;
    }
    // Atualizar os dados locais
    planeamento_data.value[index] = {
      ...planeamento_data.value[index],
      inicio: data.start.value,
      fim: data.end.value,
    };
    // Fazer a chamada API para atualizar o backend
    const res = await update(
      `${baseURL}/api/v1/planeamento/editar/${planeamento_data.value[index].id}/`,
      planeamento_data.value[index]
    );
    if (res) {
      if (planeamento_data.value[index].ordemServico_id !== 0) {
        const updatedServico = await update(
          `${baseURL}/api/v1/ordemservico/editar/${planeamento_data.value[index].ordemServico_id}/`,
          {
            data_inicio: data.start.value,
            data_fim: data.end.value,
            // status:'remarcado'
            //status: "agendado",
          }
        );
        if (updatedServico) {
          console.log("ordemServico atualizados com sucesso:", res);
          emit("refreshTable");
        }
      }

      console.log("Dados atualizados com sucesso:", res);
      emit("refreshTable");
    } else {
      console.error("Falha ao atualizar dados no servidor.");
    }
  } else {
    console.error("Evento não encontrado em planeamento_data.");
  }
};

const handleEventDeletion = async (event) => {
  const data = event.data;
  const index = planeamento_data.value.findIndex(
    (item) => item.event_id === data.id
  );
  if (index !== -1) {
    const currentStatus = planeamento_data.value[index].status;
    // Remover o evento de planeamento_data
    const deletedItem = planeamento_data.value.splice(index, 1)[0];
    // Chamar o método remove para deletar do backend

    //status atual do planeamento
    
    const res = await remove(
      `${baseURL}/api/v1/planeamento/eliminar/${deletedItem.id}/`
    );
    if (res) {
      console.log("Evento eliminado com sucesso:", res);

      if (deletedItem.ordemServico_id !== 0) {
        const update_data =
          currentStatus == "cancelado" || currentStatus == "rejeitado"
            ? {}
            : { status: "ativo" };
        const updatedServico = await update(
          `${baseURL}/api/v1/ordemservico/editar/${deletedItem.ordemServico_id}/`,
          update_data
        );
        if (updatedServico) {
          console.log("ordemServico atualizados com sucesso:", res);
          emit("refreshTable");
        }
      }
      toast("Evento eliminado com sucesso!", {
        theme: "auto",
        type: "success",
        position: "top-center",
        autoClose: 1000,
      });
      //emit('refreshTable');
    } else {
      toast("Erro ao eliminar evento!", {
        theme: "auto",
        type: "error",
        position: "top-center",
        autoClose: 1000,
      });
      // Opcionalmente, re-adicionar o evento ao calendário
      calendarRef.value.control.events.add(event);
    }
  } else {
    console.error("Evento não encontrado em planeamento_data.");
  }
};

const handleEventAction = async (event, status) => {
  const data = event.data;
  const index = planeamento_data.value.findIndex(
    (item) => item.event_id === data.id
  );
  if (index !== -1) {
    // Atualizar o status do evento
    //get current planeamento status
    const currentStatus = planeamento_data.value[index].status;
    planeamento_data.value[index] = {
      ...planeamento_data.value[index],
      status: status,
    };
    // Fazer a chamada API para atualizar o backend
    const res = await update(
      `${baseURL}/api/v1/planeamento/editar/${planeamento_data.value[index].id}/`,
      planeamento_data.value[index]
    );
    if (res) {
      if (planeamento_data.value[index].ordemServico_id !== 0) {
        const update_data=(currentStatus == 'cancelado' || currentStatus == 'rejeitado') ? {  } : { status: "remarcar" };
        const updatedServico = await update(
          `${baseURL}/api/v1/ordemservico/editar/${planeamento_data.value[index].ordemServico_id}/`,
          update_data
        );
        if (updatedServico) {
          console.log("ordemServico atualizados com sucesso:", res);

          toast(`Evento ${status} com sucesso!`, {
            theme: "auto",
            type: "success",
            position: "top-center",
            autoClose: 1000,
          });

          emit("refreshTable");
          calendarConfig.events = calendarConfig.events.map((event) => {
            if (event.id === data.id) {
              event.backColor =
                status === "cancelado"
                  ? "#999999"
                  : status === "rejeitado"
                  ? "#970000"
                  : "#dbb73e";
            }
            return event;
          });
          planeamento_descricao.value = {
            planeamento_id: data.id,
            descricao: planeamento_data.value[index].descricao,
          };

          showPopUp.value = true;
        }
      }
      //emit('refreshTable');
    } else {
      console.error("Falha ao cancelar evento.");
      toast("Erro", {
        theme: "auto",
        type: "error",
        position: "top-center",
        autoClose: 1000,
      });
    }
  } else {
    console.error("Evento não encontrado em planeamento_data.");
  }
  const eventIndex = calendarConfig.events.findIndex((e) => e.id === data.id);
  if (eventIndex !== -1) {
    calendarConfig.events[eventIndex].status = status;
    calendarRef.value.control.events.update(calendarConfig.events[eventIndex]);
  }
  calendarRef.value.control.update(); // Refresh the calendar
};

const handleEventConfirmation = async (event) => {
  const data = event.data;
  const index = planeamento_data.value.findIndex(
    (item) => item.event_id === data.id
  );
  if (index !== -1) {
    if (planeamento_data.value[index].status !== "agendado") {
      toast("Só é possível confirmar planeamentos com status 'agendado'!", {
        theme: "auto",
        type: "warning",
        position: "top-center",
        autoClose: 2000,
      });
      return;
    }
    // Atualizar o status do evento
    planeamento_data.value[index] = {
      ...planeamento_data.value[index],
      status: "confirmado",
    };
    // Fazer a chamada API para atualizar o backend
    const res = await update(
      `${baseURL}/api/v1/planeamento/editar/${planeamento_data.value[index].id}/`,
      planeamento_data.value[index]
    );
    if (res) {
      if (planeamento_data.value[index].ordemServico_id !== 0) {
        const updatedServico = await update(
          `${baseURL}/api/v1/ordemservico/editar/${planeamento_data.value[index].ordemServico_id}/`,
          {
            status: "confirmado",
          }
        );
        if (updatedServico) {
          console.log("ordemServico atualizados com sucesso:", res);

          toast(`Evento confirmado com sucesso!`, {
            theme: "auto",
            type: "success",
            position: "top-center",
            autoClose: 1000,
          });
          emit("refreshTable");
          calendarConfig.events = calendarConfig.events.map((event) => {
            if (event.id === data.id) {
              event.backColor = "#01BA32";
            }
            return event;
          });
          planeamento_descricao.value = {
            planeamento_id: data.id,
            descricao: planeamento_data.value[index].descricao,
          };
          showPopUp.value = true;
        } else {
          console.error("Falha ao confirmar evento.");
          toast("Erro", {
            theme: "auto",
            type: "error",
            position: "top-center",
            autoClose: 1000,
          });
        }
      }
    } else {
      console.error("Falha ao confirmar evento.");
      toast("Erro", {
        theme: "auto",
        type: "error",
        position: "top-center",
        autoClose: 1000,
      });
    }
  } else {
    console.error("Evento não encontrado em planeamento_data.");
  }

  const eventIndex = calendarConfig.events.findIndex((e) => e.id === data.id);
  if (eventIndex !== -1) {
    calendarConfig.events[eventIndex].status = "confirmado";
    calendarRef.value.control.events.update(calendarConfig.events[eventIndex]);
  }
  calendarRef.value.control.update(); // Refresh the calendar
};

// Utility Functions
const formatDateTime = (date) => {
  // Formatar a data para uso em datetime-local (ex: 2025-02-28T10:00)
  return new DayPilot.Date(date).toString("yyyy-MM-ddTHH:mm");
};

// API Interaction Functions
const select = async (url) => {
  try {
    const res = await axios.get(url);
    return res.data.data;
  } catch (error) {
    console.error("Erro ao obter dados:", error);
  }
};

const closeCreatePopUp = () => {
  showCreatePopUp.value = false;
};

const set_descricao = async (data) => {
  const index = planeamento_data.value.findIndex(
    (item) => item.event_id === planeamento_descricao.value.planeamento_id
  );
  if (index !== -1) {
    const res = await update(
      `${baseURL}/api/v1/planeamento/editar/${planeamento_data.value[index].id}/`,
      { descricao: data.descricao }
    );
    if (res) {
      console.log("Dados atualizados com sucesso:", res);
      planeamento_data.value[index] = {
        ...planeamento_data.value[index],
        descricao: data.descricao,
      };
      //update the descricao in the event
      calendarConfig.events = calendarConfig.events.map((event) => {
        if (event.id === planeamento_descricao.value.planeamento_id) {
          event.description = data.descricao;
        }
        return event;
      });
      showPopUp.value = false;
    }
  } else {
    console.error("Evento não encontrado em planeamento_data.");
  }
};

const checkTotalDay = (day) => {
  const dayStr = day.toString("yyyy-MM-dd");
  const events = planeamento_data.value.filter((item) => {
    const eventStart = new DayPilot.Date(item.inicio);
    return (
      eventStart.getDatePart().toString("yyyy-MM-dd") === dayStr &&
      !["cancelado", "rejeitado"].includes(item.status)
    );
  });
  const total = events.reduce((sum, val) => sum + val.valor, 0);

  if (total > 0) {
    if (total < props.tecnico.valor) {
      toast(`Dia ${day.toString("dd/MM/yyyy")} com valor insuficiente!`, {
        theme: "auto",
        type: "warning",
        position: "top-center",
        autoClose: 2000,
      });
    } else if (total >= props.tecnico.valor) {
      // toast(`Dia ${day.toString("dd/MM/yyyy")} com valor suficiente!`, {
      //   theme: "auto",
      //   type: "success",
      //   position: "top-center",
      //   autoClose: 2000,
      // });
    }
  } else {
    toast(`Dia ${day.toString("dd/MM/yyyy")} sem valor!`, {
      theme: "auto",
      type: "warning",
      position: "top-center",
      autoClose: 2000,
    });
  }
};

const setColor = (day) => {
  const dayStr = day.toString("yyyy-MM-dd");
  let events;
  if (props.isTecnico) {
    events = planeamento_data.value.filter((item) => {
      const eventStart = new DayPilot.Date(item.inicio);
      return (
        eventStart.getDatePart().toString("yyyy-MM-dd") === dayStr &&
        (item.status === "confirmado" ||
          item.status === "fechado" ||
          item.status === "atribuido")
      );
    });
  } else {
    events = planeamento_data.value.filter((item) => {
      const eventStart = new DayPilot.Date(item.inicio);
      return eventStart.getDatePart().toString("yyyy-MM-dd") === dayStr;
    });
  }

  const total = events.reduce((sum, val) => sum + val.valor, 0);
  if (total > 0) {
    if (total < props.tecnico.valor) {
      return "#970000";
    } else if (total >= props.tecnico.valor) {
      return "green";
    }
  } else return "#970000";
};
const createServico = async (data) => {
  return await requestLock.execute('create-servico', async () => {
    const res = await create(
    data,
    `${baseURL}/api/v1/ordemservico/planeamento/registrar/normal/`
  );
  if (res) {
    console.log("Dados criados com sucesso:", res);
    //showCreatePopUp.value = false;
    toast("Serviço criado com sucesso!", {
      theme: "auto",
      type: "success",
      position: "top-center",
      autoClose: 1000,
    });
    let servico = await select(`${baseURL}/api/v1/ordemservico/${res.id}/`);
    console.log("created Servico", servico);
    servico = servico.data;
    let endereco = "";
    if (servico.endereco.id != 0) {
      if (servico.endereco?.morada) {
        endereco += servico.endereco.morada;
      }
      if (servico.endereco?.distrito) {
        endereco += ", " + servico.endereco.distrito;
      }
      if (servico.endereco?.codigoPostal) {
        endereco += "," + servico.endereco.codigoPostal;
      }
    } else {
      endereco = "Sem Endereço";
    }
    //const endereco =servico.endereco.id!=0? (servico.endereco?.morada + ', ' + servico.endereco?.distrito + '-' + servico.endereco?.codigoPostal):'Sem Endereço';

    if (servico.status == "atribuido") {
      const start = servico.data_inicio;
      const end = servico.data_fim;
      const servicoId = servico.id;
      const titulo = `Serviço: ${servico.Familia.nome}\n
      Cliente: ${servico.cliente.cliente}\n
      SubCliente: ${servico.subCliente.nome}\n
      Local: ${servico.Negocio.localInstalacao}\n
      Endereço: ${endereco}\n
      Valor: ${servico.valor_previsto}
      `;
      const descricao = servico.Negocio.descricao || "";
      const utilizadorId = servico.Utilizador.id;
      const tecnicoId = servico.Utilizador.id;
      const eventId = DayPilot.guid();
      const valor = servico.valor_previsto || 0;

      const event = {
        id: eventId,
        start: start,
        end: end,
        Servico: servico.Familia.nome,
        Cliente: servico.cliente.cliente,
        SubCliente: servico.subCliente.nome,
        Local: servico.Negocio.localInstalacao,
        Endereco: `${endereco}`,
        Valor: valor,
        text: titulo,
        description: descricao,
        service: servico.Familia.nome,
        location: servico.Negocio.localInstalacao,
        status: "pendente",
        backColor : "#dbb73e"
      };

      

      const planeamentoData = {
        event_id: eventId,
        titulo: event.text,
        inicio: event.start,
        fim: event.end,
        zona_id: props.zona.id,
        servico_id: servicoId,
        descricao: event.description,
        utilizador_id: utilizadorId,
        tecnico_id: tecnicoId,
        ordemServico_id: servico.id,
        data: new Date().toISOString(),
        valor: valor,
        status: "pendente",
      };

      const res = await create(
        planeamentoData,
        `${baseURL}/api/v1/planeamento/registrar/`
      );
      if (res) {
        console.log("Planeamento criado com sucesso:", res);
        planeamento_data.value.push({
          ...res,
          zona: { id: props.zona.id, nome: props.zona.nome },
          servico: { id: servicoId, nome: servico.Familia.nome },
        });
        // mudar a cor do evento para pendente
        // Adicionar o evento ao calendário
        calendarConfig.events.push(event);

        
        toast("Planeamento criado com sucesso!", {
          theme: "auto",
          type: "success",
          position: "top-center",
          autoClose: 1000,
        });
      } else {
        toast("Erro ao criar planeamento!", {
          theme: "auto",
          type: "error",
          position: "top-center",
          autoClose: 1000,
        });
      }
    }
    showCreatePopUp.value = false;
    console.log("Servico", servico);
    emit("servicoCreated", servico);
    emit("refreshTable");
  } else {
    toast("Erro ao criar serviço!", {
      theme: "auto",
      type: "error",
      position: "top-center",
      autoClose: 1000,
    });
  }
  });
};

const create = async (data, url) => {
  try {
    const res = await axios.post(url, data);
    return res.data.data;
  } catch (error) {
    console.error("Erro ao criar dados:", error);
  }
};

const select_all = async (url) => {
  try {
    const res = await axios.get(url);
    return res.data.data;
  } catch (error) {
    console.error("Erro ao obter dados:", error);
  }
};

const update = async (url, data) => {
  try {
    const res = await axios.put(url, data);
    console.log("Dados atualizados com sucesso:", res.data);
    return res.data.data;
  } catch (error) {
    console.error("Erro ao atualizar dados:", error);
  }
};

const remove = async (url) => {
  try {
    await axios.delete(url);
    return true;
  } catch (error) {
    console.error("Erro ao deletar dados:", error);
  }
};

// Lifecycle Hook
onMounted(() => {
  const today = new DayPilot.Date();
  calendarConfig.startDate = today.firstDayOfWeek(1); // Alinhar à segunda-feira
  loadEvents();
});
</script>

<style>
.wrap {
  display: flex;
}

.left {
  margin-right: 10px;
}

.content {
  flex-grow: 1;
}

/* Existing styles */

.calendar_default_event_inner {
  background: #2e78d6;
  border-color: #0a5cd2;
  color: white;
  border-radius: 5px;
  opacity: 0.9;
}

.calendar_default_main {
  background-color: #000000;
}

.calendar_default_cell {
  background-color: #000000;
}

/* Styles for Summary Section */
.summary {
  display: flex;
  margin-left: 3.74rem;
  /* Ajustar conforme necessário para alinhar com o calendário */
}

.summary-column {
  flex: 1;
  max-width: 15rem;
  background-color: #5d5d98;

  border-left: 1px solid white;
  border-bottom: 1px solid white;
  border-right: 1px solid white;

  text-align: center;
  padding: 0.5rem;
  /* Adicionar padding para melhor espaçamento */
}

.summary-column:last-child {
  margin-right: 0;
}

.day-name {
  font-weight: bold;
  margin-bottom: 2px;
  /* Espaçamento entre o nome do dia e o valor */
}

.day-valor {
  margin-bottom: 10px;
  /* Espaçamento entre o valor e o botão */
}

.summary-column button {
  padding: 5px 6px;
  background-color: #ff9b05;
  color: white;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  font-size: 0.8rem;
}

.summary-column button:hover {
  background-color: #e68a00;
  /* Levemente mais escuro ao passar o mouse para melhor UX */
}


</style>
